import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { WorkbenchBreadcrumbConfig } from '@app/models/breadcrumb.model';
import { Router } from '@angular/router';
import { ClosedownService } from '@app/services/closedown/closedown.service';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { Company } from '@app/models/user.model';
import { AuthService } from '@app/services/auth/auth.service';
import { BrowserService } from '@app/services/browser/browser.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-resource-admin-dashboard',
  templateUrl: './resource-admin-dashboard.component.html',
  styleUrls: ['./resource-admin-dashboard.component.scss'],
  standalone: false
})
export class ResourceAdminDashboardComponent implements OnInit, OnDestroy {
  isRedirectingToWorkbench = false;
  readonly currentProfileCompany = signal<Company | null>(null);
  private readonly authService = inject(AuthService);
  private readonly browserService = inject(BrowserService);
  readonly dashboardCounts = {
    openJobs: 0,
    pendingApprovalJobs: 0,
    openPpms: 0,
    jobsAwaitingInvoice: 0,
    pendingInvoiceApproval: 4,
    recalledInvoices: 12,
    rejectedInvoices: 1
  };
  breadcrumb: WorkbenchBreadcrumbConfig;

  constructor(
    private readonly closedownService: ClosedownService,
    private readonly jobService: JobService,
    private readonly invoiceService: InvoiceService,
    private readonly navigationService: NavigationService,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {
    this.navigationService.onChange(false, false);
    this.breadcrumb = {
      title: this.translate.instant("resource_admin_dashboard_title"),
      subtitle: this.translate.instant("resource_admin_dashboard_description")
    };
  }

  ngOnInit(): void {
    void this.loadCurrentProfileCompany();
    this.jobService.getDashboardSummary().subscribe({
      next: summary => {
        this.dashboardCounts.openJobs = summary.openJobs;
        this.dashboardCounts.pendingApprovalJobs = summary.pendingApprovalJobs;
        this.dashboardCounts.openPpms = summary.openPpms;
      },
      error: () => {
        this.dashboardCounts.openJobs = 0;
        this.dashboardCounts.pendingApprovalJobs = 0;
        this.dashboardCounts.openPpms = 0;
      }
    });

    this.invoiceService.getJobsWithApprovedReceiptPurchaseOrders().subscribe({
      next: jobs => this.dashboardCounts.jobsAwaitingInvoice = jobs?.length ?? 0,
      error: () => this.dashboardCounts.jobsAwaitingInvoice = 0
    });
  }

  ngOnDestroy(): void {
    this.navigationService.onChange(true, false);
  }

  private async loadCurrentProfileCompany(): Promise<void> {
    this.currentProfileCompany.set(null);
    try {
      const companyId = await this.authService.getCurrentProfile();
      if (companyId) {
        const companies = await this.authService.getProfileCompanies();
        this.currentProfileCompany.set(companies.find(company => company.id === companyId) ?? null);
      }
    } catch {
      this.currentProfileCompany.set(null);
    }
  }

  openPpmBulkManager(): void {
    const company = this.currentProfileCompany();
    if (company?.referenceNumber) {
      const url = new URL('/ppmbulkmanager', `${window.location.protocol}//${environment.appDomain}`);
      url.searchParams.set('rid', company.referenceNumber);
      this.browserService.openExternalhref(url.toString());
    }
  }

  openWorkbench(): void {
    if (this.isRedirectingToWorkbench) {
      return;
    }

    this.isRedirectingToWorkbench = true;
    void this.closedownService.directToClosedown(false);
  }

  openJobs(): void {
    void this.router.navigate(['openjobs']);
  }

  openJobsAwaitingInvoice(): void {
    void this.router.navigate(['jobsawaitinginvoice']);
  }

  openSubmittedInvoices(): void {
    void this.router.navigate(['submittedinvoices']);
  }

  openUpgradeSummary(): void {
    void this.router.navigate(['upgradesummary']);
  }

  openUserManagement(): void {
    void this.router.navigate(['usermanagementlist']);
  }
}